import React, { useState, useEffect } from 'react';
import { Alert, Modal, Form, Button, ProgressBar, OverlayTrigger, Tooltip } from 'react-bootstrap';
import { readContract, writeContract, getWalletClient, waitForTransactionReceipt } from '@wagmi/core';
import communityconsensusContractABI from '../../contracts/tgetherCommunityConsensus.json';
import LinkTokenABI from '../../contracts/LinkToken.json';

import { formatEther } from 'viem'; // For ETH <-> Wei conversion
import { useNavigate } from 'react-router-dom';
import * as config from '../../config.tsx';

const communityconsensusContractAddress = process.env.REACT_APP_COMMUNITY_CONSENSUS_CONTRACT_ADDRESS as `0x${string}`;
const linkTokenContract = process.env.REACT_APP_CHAINLINK_TOKEN_CONTRACT as `0x${string}`;

function ConsensusProposalModal({ show, setShow, communityName }: any) {
  const [currentStep, setCurrentStep] = useState(1);
  const [showWaitAlert, setShowWaitAlert] = useState(false);
  const [feeChecked, setFeeChecked] = useState(false);
  const [feeInEth, setFeeInEth] = useState<string>(''); // Fee displayed in ETH
  const [createFormData, setCreateFormData] = useState({
    numReviewsForAcceptance: '0',
    credsNeededForReview: '0',
    percentAcceptsNeeded: '0',
    consensusTime: '0',
    consensusTypes: '',
    feeInWei: '0', // Fee stored in Wei for the transaction
  });

  const navigate = useNavigate();

  const handleCreateInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setCreateFormData(prevState => ({
      ...prevState,
      [name]: value,
    }));
  };

  const handleNext = () => {
    if (currentStep < 6) {
      setCurrentStep(prevStep => prevStep + 1);
    }
  };

  const handlePrev = () => {
    if (currentStep > 1) {
      setCurrentStep(prevStep => prevStep - 1);
    }
  };

  const approveLinkFee= async () => {

    const tx = await writeContract(config.config, {
      address: linkTokenContract,   // LINK token contract address
      abi: LinkTokenABI,            // Correct ABI for the LINK token
      functionName: 'approve',      // The function name (approve)
      args: [ process.env.REACT_APP_FUND_CONTRACT, BigInt("200000000000000000")],
      value: BigInt("0"),           // No value for this transaction
    });

    // Wait for the transaction to be mined
    const receipt = await waitForTransactionReceipt(config.config,{
      hash: tx,  // Transaction hash from the tx response
    });
    setFeeInEth(formatEther(BigInt("0")));
    setFeeChecked(true)
  }


  // Fetch the fee from the contract using readContract
  const calculateFee = async () => {
    try {
      const feeData = await readContract(config.config, {
        abi: communityconsensusContractABI.abi,
        address: communityconsensusContractAddress,
        functionName: 'totalFee',
      });

      const feeInWei = BigInt(Number(feeData).toString());
      const feeInEth = formatEther(feeInWei); // Convert Wei to ETH for display

      setFeeInEth(feeInEth);

      console.log(feeInEth)

      setCreateFormData(prevState => ({
        ...prevState,
        feeInWei: feeInWei.toString(), // Store in Wei for the transaction
      }));
      setFeeChecked(true);
    } catch (error) {
      console.error('Error fetching fee from contract:', error);
    }
  };

  const handleCreateConsensusProposalModal = async (e: React.FormEvent) => {
    e.preventDefault();

    try {
      // Get the wallet client
      const walletClient = await getWalletClient(config.config);
      if (!walletClient) {
        console.error('No wallet client available.');
        return;
      }

      const consensusTypesArray = createFormData.consensusTypes.split(',').map(type => type.trim());

      // Send the transaction using writeContract
      const tx = await writeContract(config.config, {
        abi: communityconsensusContractABI.abi,
        address: communityconsensusContractAddress,
        functionName: 'CreateCCProposal',
        args: [
          communityName,
          Number(createFormData.numReviewsForAcceptance),
          Number(createFormData.credsNeededForReview),
          Number(createFormData.percentAcceptsNeeded),
          Number(createFormData.consensusTime),
          consensusTypesArray,
        ],
        value: BigInt(createFormData.feeInWei), // Send the fee in Wei
      });

      setShowWaitAlert(true);

      // Wait for the transaction receipt
      const receipt = await waitForTransactionReceipt(config.config, {
        hash: tx,
      });

      console.log('Transaction receipt:', receipt);
      setShow(false);
      const hexValue = receipt.logs[0].topics[1]; // example: "0x0000000000000000000000000000000000000000000000000000000000000006"
      console.log('Proposal created:', hexValue);
      const numberValue = Number(hexValue); // Converts hex to integer
      navigate(`/search-results?type=Proposal&value=${encodeURIComponent(numberValue)}`);
    } catch (error) {
      console.error('Error creating consensus proposal:', error);
    }
  };

  const renderTooltip = (text: string) => (
    <Tooltip id="button-tooltip">{text}</Tooltip>
  );
  const renderStep = () => {
    switch (currentStep) {
      case 1:
        return (
          <Form.Group>
            <Form.Label>Number of Reviews for Acceptance</Form.Label>
            <OverlayTrigger
              placement="right"
              overlay={renderTooltip('This value sets the number of reviews a proposal must receive for acceptance.')}
            >
              <Button variant="secondary" className="ml-1 p-0" style={{ border: 'none', background: 'none' }}>
                <i className="bi bi-patch-question-fill"></i>
              </Button>
            </OverlayTrigger>
            <Form.Control
              type="number"
              name="numReviewsForAcceptance"
              value={createFormData.numReviewsForAcceptance}
              onChange={handleCreateInputChange}
            />
          </Form.Group>
        );
      case 2:
        return (
          <Form.Group>
            <Form.Label>Creds Needed for Review</Form.Label>
            <OverlayTrigger
              placement="right"
              overlay={renderTooltip('This value sets the credentials a member needs to review proposals.')}
            >
              <Button variant="secondary" className="ml-1 p-0" style={{ border: 'none', background: 'none' }}>
                <i className="bi bi-patch-question-fill"></i>
              </Button>
            </OverlayTrigger>
            <Form.Control
              type="number"
              name="credsNeededForReview"
              value={createFormData.credsNeededForReview}
              onChange={handleCreateInputChange}
            />
          </Form.Group>
        );
      case 3:
        return (
          <Form.Group>
            <Form.Label>Percentage of Accepts Needed</Form.Label>
            <OverlayTrigger
              placement="right"
              overlay={renderTooltip('This value sets the percentage of accepts needed for a proposal to pass.')}
            >
              <Button variant="secondary" className="ml-1 p-0" style={{ border: 'none', background: 'none' }}>
                <i className="bi bi-patch-question-fill"></i>
              </Button>
            </OverlayTrigger>
            <Form.Control
              type="number"
              name="percentAcceptsNeeded"
              value={createFormData.percentAcceptsNeeded}
              onChange={handleCreateInputChange}
            />
          </Form.Group>
        );
      case 4:
        return (
          <Form.Group>
            <Form.Label>Consensus Time (in seconds)</Form.Label>
            <OverlayTrigger
              placement="right"
              overlay={renderTooltip('This value sets the time a proposal will remain active for consensus.')}
            >
              <Button variant="secondary" className="ml-1 p-0" style={{ border: 'none', background: 'none' }}>
                <i className="bi bi-patch-question-fill"></i>
              </Button>
            </OverlayTrigger>
            <Form.Control
              type="number"
              name="consensusTime"
              value={createFormData.consensusTime}
              onChange={handleCreateInputChange}
            />
          </Form.Group>
        );
      case 5:
        return (
          <Form.Group>
            <Form.Label>Consensus Types (comma-separated)</Form.Label>
            <OverlayTrigger
              placement="right"
              overlay={renderTooltip('Enter the types of consensus mechanisms applicable to this proposal, separated by commas.')}
            >
              <Button variant="secondary" className="ml-1 p-0" style={{ border: 'none', background: 'none' }}>
                <i className="bi bi-patch-question-fill"></i>
              </Button>
            </OverlayTrigger>
            <Form.Control
              type="text"
              name="consensusTypes"
              value={createFormData.consensusTypes}
              onChange={handleCreateInputChange}
              placeholder="Type1,Type2,Type3"
            />
          </Form.Group>
        );
      case 6:
        if (process.env.REACT_APP_CHAIN_ID == "0x66eee"){
          return(
            <div>
            <p>Press the button to approve the testnet LINK transfer. You can go to 
              <a href="https://faucets.chain.link/arbitrum-sepolia" target="_blank" rel="noopener noreferrer">
                Arbitrum Sepolia Faucet
              </a> to get funds.</p>
            <Button style={{marginBottom:"10px"}}variant="outline-info" onClick={approveLinkFee}>
              Approve 0.2 LINK for Fee
            </Button>
          </div>


          )

        }else{
        return (
          <Form.Group>
            <Form.Label>
              Fee{' '}
              <OverlayTrigger
                placement="right"
                overlay={renderTooltip(
                  'This fee covers the gas cost and is automatically calculated based on the current network conditions and the contract’s requirements.'
                )}
              >
                <Button variant="secondary" className="ml-1 p-0" style={{ border: 'none', background: 'none' }}>
                  <i className="bi bi-patch-question-fill"></i>
                </Button>
              </OverlayTrigger>
            </Form.Label>
            <div style={{ display: 'flex', alignItems: 'center', marginBottom: '20px' }}>
              <Button variant="info" style={{ marginRight: '10px' }} onClick={calculateFee}>
                Calculate Fee
              </Button>
              <p style={{ color: '#6c757d', fontSize: '.9rem' }}>
                *Verify before signing the transaction that the value matches below. This fee covers automated transactions.
              </p>
            </div>
            <Form.Control
              type="text"
              name="fee"
              value={feeInEth} // Use the formatted totalFee or fallback to manual input
              readOnly
            />
          </Form.Group>
        );
      }
      default:
        return null; 
    }
  };

  return (
    <>
      {showWaitAlert && (
        <Alert variant="warning" onClose={() => setShowWaitAlert(false)} dismissible>
          Creating proposal, please wait...
        </Alert>
      )}
      <Modal show={show} onHide={() => setShow(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Create a Consensus Proposal</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form onSubmit={handleCreateConsensusProposalModal}>
            {renderStep()}
            <ProgressBar now={(currentStep / 6) * 100} />
            <div className="mt-3 d-flex justify-content-between">
              <Button variant="outline-secondary" onClick={handlePrev} disabled={currentStep === 1}>
                Previous
              </Button>
              {currentStep < 6 ? (
                <Button variant="info" onClick={handleNext}>
                  Next
                </Button>
              ) : (
                <Button variant="info" type="submit" disabled={!feeChecked}>
                  Create Proposal
                </Button>
              )}
            </div>
          </Form>
        </Modal.Body>
      </Modal>
    </>
  );
}

export default ConsensusProposalModal;